import { Trans, useLingui } from "@lingui/react";
import { TradeOrgPages } from "src/types/tradeorgPages.ts";
import { Logo } from "./Logo.tsx";

interface Props {
  pageName: TradeOrgPages;
}

const OrgLinks = ({ pageName }: Props) => {
  const imageTitle = useImageTitle(pageName);
  const tradeOrgLinks = useTradeOrgLinks(pageName);

  return (
    <div className="mt-40 md:mt-0 pb-40 md:pb-0">
      <Logo pageName={pageName} />

      {imageTitle && <h4 className="t4 mt-16 mb-0">{imageTitle}</h4>}

      {tradeOrgLinks.length > 0 && (
        <div className="p-16 mt-24 border s-border rounded-8">
          <h3 className="t3 mb-0">
            <Trans id="tradeOrgLayout.orgLinks.title" message="Useful links" comment="Title for the useful links box" />
          </h3>

          {tradeOrgLinks.map((link) => (
            <p key={link.url} className="mt-16 mb-0">
              <a href={link.url} target="_blank" rel="noopener">
                {link.text}
              </a>
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrgLinks;

const useImageTitle = (pageName: TradeOrgPages) => {
  const { i18n } = useLingui();

  switch (pageName) {
    case TradeOrgPages.NBF:
      return i18n.t({
        id: "tradeOrgLayout.nbf.links.title",
        message: "Norwegian Automobile Industry Association",
        comment: "Do not edit the translation submitted. Pre-approved text, must not deviate.",
      });
    case TradeOrgPages.Norboat:
      return i18n.t({
        id: "tradeOrgLayout.norboat.links.title",
        message: "Norboat - The Norwegian Boating Industry Association",
        comment: "Do not edit the translation submitted. Pre-approved text, must not deviate.",
      });
    case TradeOrgPages.NCB:
      return i18n.t({
        id: "tradeOrgLayout.ncb.links.title",
        message: "Norwegian Caravan Industry Association",
        comment: "Do not edit the translation submitted. Pre-approved text, must not deviate.",
      });
    case TradeOrgPages.NMCF:
      return i18n.t({
        id: "tradeOrgLayout.nmcf.links.title",
        message: "Norwegian MC Dealers Association",
        comment: "Do not edit the translation submitted. Pre-approved text, must not deviate.",
      });
    case TradeOrgPages.MobilityPool:
      return i18n.t({
        id: "tradeOrgLayout.mobilitypool.links.title",
        message: "Mobility Pool",
        comment: "Do not edit the translation submitted. Pre-approved text, must not deviate.",
      });
    case TradeOrgPages.Bruktbilgruppen:
      return i18n.t({
        id: "tradeOrgLayout.bruktbilgruppen.links.title",
        message: "Bruktbilgruppen",
        comment: "Do not edit the translation submitted. Pre-approved text, must not deviate.",
      });
    case TradeOrgPages.MRF:
      return "Motorbranschens Riksförbund (MRF)";

    case TradeOrgPages.Sweboat:
      return "Sweboat";

    case TradeOrgPages.NBAS:
      return "Norsk Bransjeforening for ATV og Snøscooter";
    default:
      return null;
  }
};

const useTradeOrgLinks = (pageName: TradeOrgPages) => {
  const { i18n } = useLingui();

  switch (pageName) {
    case TradeOrgPages.NBF: {
      const linkOneText = i18n.t({
        id: "tradeOrgLayout.nbf.link.one",
        message: "More about the benefits of shopping with an NBF member",
        comment: "Do not edit the translation submitted. Pre-approved text, must not deviate.",
      });
      const linkTwoText = i18n.t({
        id: "tradeOrgLayout.nbf.link.two",
        message: "Cars with condition report with warranty",
        comment: "Do not edit the translation submitted. Pre-approved text, must not deviate.",
      });

      return [
        { url: "https://www.nbf.no/om-oss/handle-trygt/", text: linkOneText },
        {
          url: "https://www.finn.no/mobility/search/car?car_condition=2&warranty_insurance=1",
          text: linkTwoText,
        },
      ];
    }

    case TradeOrgPages.Norboat: {
      const linkOneText = i18n.t({
        id: "tradeOrgLayout.norboat.link.one",
        message: "Overview of all Norboat's members",
        comment: "Do not edit the translation submitted. Pre-approved text, must not deviate.",
      });
      const linkTwoText = i18n.t({
        id: "tradeOrgLayout.norboat.link.two",
        message: "About Norboat Authorization",
        comment: "Do not edit the translation submitted. Pre-approved text, must not deviate.",
      });
      const linkThreeText = i18n.t({
        id: "tradeOrgLayout.norboat.link.three",
        message: 'The boat fair "Boats in the sea"',
        comment: "Do not edit the translation submitted. Pre-approved text, must not deviate.",
      });
      const linkFourText = i18n.t({
        id: "tradeOrgLayout.norboat.link.four",
        message: 'Boat fair "The sea for all"',
        comment: "Do not edit the translation submitted. Pre-approved text, must not deviate.",
      });

      return [
        { url: "https://norboat.no/medlemmer", text: linkOneText },
        { url: "https://norboat.no/autorisasjon", text: linkTwoText },
        { url: "https://baterisjoen.no/", text: linkThreeText },
        { url: "https://sjoenforalle.no/", text: linkFourText },
      ];
    }

    case TradeOrgPages.NCB:
      return [];

    case TradeOrgPages.NMCF: {
      const linkOneText = i18n.t({
        id: "tradeOrgLayout.nmcf.link.one",
        message: "FIND your local NMCF dealer",
        comment: "Do not edit the translation submitted. Pre-approved text, must not deviate.",
      });
      const linkTwoText = i18n.t({
        id: "tradeOrgLayout.nmcf.link.two",
        message: "Read more about NMCF Authorization",
        comment: "Do not edit the translation submitted. Pre-approved text, must not deviate.",
      });

      return [
        { url: "https://www.nmcf.no/forhandlere/", text: linkOneText },
        { url: "https://www.nmcf.no/autorisasjon/", text: linkTwoText },
      ];
    }

    case TradeOrgPages.MobilityPool: {
      const linkOneText = i18n.t({
        id: "tradeOrgLayout.mobilitypool.link.one",
        message: "More about Mobility Pool",
        comment: "Do not edit the translation submitted. Pre-approved text, must not deviate.",
      });
      return [{ url: "https://mobilitypool.no/", text: linkOneText }];
    }

    case TradeOrgPages.Bruktbilgruppen: {
      const linkOneText = i18n.t({
        id: "tradeOrgLayout.bruktbilgruppen.link.one",
        message: "More about the benefits of buying from a certified member of the Bruktbilgruppen",
        comment: "Do not edit the translation submitted. Pre-approved text, must not deviate.",
      });

      return [{ url: "https://bruktbilgruppen.no/sertifisertmedlem/", text: linkOneText }];
    }

    case TradeOrgPages.MRF: {
      return [{ url: "https://www.mrf.se/privatperson/", text: "Läs mer om MRF" }];
    }

    case TradeOrgPages.Sweboat: {
      return [{ url: "https://www.sweboat.se/", text: "Läs mer om Sweboat" }];
    }

    default:
      return [];
  }
};
